import React, { useState, useEffect } from 'react';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, TouchSensor } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import Card from '../Card';
import { Card as CardType, CardColor } from '../../types';
import BoardContainer from './components/BoardContainer';
import AddCardButton from './components/AddCardButton';
import ResetButton from './components/ResetButton';
import TagFilter from './components/TagFilter';

// ローカルストレージのキー
const STORAGE_KEY = 'card-board-state';

// デフォルトのカードサイズ
const DEFAULT_CARD_WIDTH = 200;
const DEFAULT_CARD_HEIGHT = 150;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UI_Z_INDEX = 9999;

const Board: React.FC = () => {
  const [cards, setCards] = useState<CardType[]>([]);
  const [maxZIndex, setMaxZIndex] = useState<number>(0);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [lastAddedCardId, setLastAddedCardId] = useState<string | null>(null);
  
  // センサーの設定
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );
  
  // ローカルストレージから状態を復元
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        // 保存されたカードを取得
        let savedCards: CardType[] = JSON.parse(savedState).cards;
        
        // カードが存在する場合、Zインデックスを再計算
        if (savedCards && savedCards.length > 0) {
          // カードを順番に並べ替え、Zインデックスを1から順番に振り直す
          savedCards = savedCards.sort((a, b) => a.zIndex - b.zIndex)
            .map((card, index) => ({
              ...card,
              zIndex: index + 1
            }));
          
          // 画面外のカードを画面内に移動
          savedCards = savedCards.map(card => {
            let { x, y } = card.position;
            let width = card.size?.width || DEFAULT_CARD_WIDTH;
            let height = card.size?.height || DEFAULT_CARD_HEIGHT;
            let positionChanged = false;
            let sizeChanged = false;
            
            // カードのサイズが画面サイズより大きい場合は調整
            if (width > windowSize.width - 50) {
              width = windowSize.width - 50;
              sizeChanged = true;
            }
            
            if (height > windowSize.height - 50) {
              height = windowSize.height - 50;
              sizeChanged = true;
            }
            
            // 最小サイズを保証
            width = Math.max(200, width);
            height = Math.max(150, height);
            
            // 右端が画面外の場合
            if (x + width > windowSize.width) {
              x = windowSize.width - width;
              positionChanged = true;
            }
            
            // 下端が画面外の場合
            if (y + height > windowSize.height) {
              y = windowSize.height - height;
              positionChanged = true;
            }
            
            // 左端が画面外の場合
            if (x < 0) {
              x = 0;
              positionChanged = true;
            }
            
            // 上端が画面外の場合
            if (y < 0) {
              y = 0;
              positionChanged = true;
            }
            
            // 位置またはサイズが変わった場合に更新
            if (positionChanged || sizeChanged) {
              return {
                ...card,
                position: { x, y },
                size: { width, height }
              };
            }
            
            return card;
          });
          
          // 最大のZインデックスを設定
          setMaxZIndex(savedCards.length);
          
          // 再計算したカードを設定
          setCards(savedCards);
        } else {
          // カードがない場合は初期カードを追加
          addInitialCard();
        }
      } catch (error) {
        console.error('Failed to load state from localStorage:', error);
        // エラーが発生した場合も初期カードを追加
        addInitialCard();
      }
    } else {
      // ローカルストレージにデータがない場合は初期カードを追加
      addInitialCard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // 初期カードを追加する関数
  const addInitialCard = () => {
    const newZIndex = 1;
    setMaxZIndex(newZIndex);
    
    const newCardId = uuidv4();
    
    const newCard: CardType = {
      id: newCardId,
      content: '',
      position: {
        x: (windowSize.width - DEFAULT_CARD_WIDTH) / 2,
        y: (windowSize.height - DEFAULT_CARD_HEIGHT) / 2,
      },
      size: {
        width: DEFAULT_CARD_WIDTH,
        height: DEFAULT_CARD_HEIGHT
      },
      color: '#ffffff',
      tags: [],
      zIndex: newZIndex,
    };
    
    setCards([newCard]);
    setLastAddedCardId(newCardId);
    
    // 次のレンダリングサイクルでフォーカスが適用された後、IDをリセット
    setTimeout(() => {
      setLastAddedCardId(null);
    }, 100);
  };
  
  // カードの状態が変更されたらローカルストレージに保存
  useEffect(() => {
    if (cards.length > 0) {
      const state = {
        cards
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [cards]);
  
  // ウィンドウのリサイズを監視し、カードが画面外に出ないように調整
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleWindowResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      setWindowSize({
        width: newWidth,
        height: newHeight
      });
      
      // リサイズ後に画面外に出たカードを画面内に戻す
      setCards(prevCards => {
        let needsUpdate = false;
        const updatedCards = prevCards.map(card => {
          let { x, y } = card.position;
          const cardWidth = card.size?.width || DEFAULT_CARD_WIDTH;
          const cardHeight = card.size?.height || DEFAULT_CARD_HEIGHT;
          let positionChanged = false;

          // 右端が画面外の場合
          if (x + cardWidth > newWidth) {
            x = newWidth - cardWidth;
            positionChanged = true;
          }

          // 下端が画面外の場合
          if (y + cardHeight > newHeight) {
            y = newHeight - cardHeight;
            positionChanged = true;
          }

          // 左端が画面外の場合
          if (x < 0) {
            x = 0;
            positionChanged = true;
          }

          // 上端が画面外の場合
          if (y < 0) {
            y = 0;
            positionChanged = true;
          }

          // 位置が変わった場合のみ更新
          if (positionChanged) {
            needsUpdate = true;
            return {
              ...card,
              position: { x, y },
            };
          }

          return card;
        });

        return needsUpdate ? updatedCards : prevCards;
      });
    };
    
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);
  
  // ボードをリセット（すべてのカードを削除）
  const resetBoard = () => {
    if (window.confirm('すべてのカードを削除してもよろしいですか？')) {
      localStorage.removeItem(STORAGE_KEY);
      setCards([]);
      setMaxZIndex(0);
      setActiveTag(null);
    }
  };
  
  // タグでフィルタリングする
  const filterByTag = (tag: string) => {
    if (activeTag === tag) {
      // 同じタグをもう一度クリックした場合はフィルターを解除
      setActiveTag(null);
    } else {
      setActiveTag(tag);
    }
  };
  
  // すべてのタグを取得
  const getAllTags = () => {
    const tagsSet = new Set<string>();
    cards.forEach(card => {
      card.tags.forEach(tag => {
        tagsSet.add(tag);
      });
    });
    return Array.from(tagsSet);
  };
  
  // フィルタリングされたカードを取得
  const getFilteredCards = () => {
    if (!activeTag) return cards;
    return cards.filter(card => card.tags.includes(activeTag));
  };

  // Zインデックスを再整理する関数
  const reorganizeZIndices = (targetCardId?: string) => {
    // カードをZインデックスでソート
    const sortedCards = [...cards].sort((a, b) => a.zIndex - b.zIndex);
    
    // 新しいZインデックスを割り当て
    const updatedCards = sortedCards.map((card, index) => ({
      ...card,
      zIndex: index + 1
    }));
    
    // 対象のカードがある場合は最前面に
    if (targetCardId) {
      const targetCard = updatedCards.find(card => card.id === targetCardId);
      if (targetCard) {
        // 対象のカードを最前面に
        targetCard.zIndex = updatedCards.length + 1;
        // 最大Zインデックスを更新
        setMaxZIndex(updatedCards.length + 1);
      } else {
        // 対象のカードがない場合は、最大Zインデックスをカードの数に設定
        setMaxZIndex(updatedCards.length);
      }
    } else {
      // 対象のカードがない場合は、最大Zインデックスをカードの数に設定
      setMaxZIndex(updatedCards.length);
    }
    
    setCards(updatedCards);
  };

  // カードを最前面に持ってくる関数
  const bringToFront = (id: string) => {
    // 現在のカードの状態を取得
    const targetCard = cards.find(card => card.id === id);
    if (!targetCard) return;
    
    // Zインデックスが一定値を超えたら再整理
    if (maxZIndex > 100) {
      reorganizeZIndices(id);
      return;
    }
    
    // 他のカードのZインデックスはそのままで、対象のカードだけを最前面に
    const newZIndex = maxZIndex + 1;
    setMaxZIndex(newZIndex);
    
    setCards(prevCards => 
      prevCards.map(card => 
        card.id === id ? { ...card, zIndex: newZIndex } : card
      )
    );
  };

  // カードのドラッグ終了時の処理
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    const cardId = active.id as string;
    
    setCards((prevCards) =>
      prevCards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            position: {
              x: card.position.x + delta.x,
              y: card.position.y + delta.y,
            },
          };
        }
        return card;
      })
    );
    
    // ドラッグ終了時にカードを最前面に
    bringToFront(cardId);
  };

  // 新しいカードを追加
  const addNewCard = () => {
    // 新しいカードのZインデックスは現在の最大値+1
    const newZIndex = maxZIndex + 1;
    setMaxZIndex(newZIndex);
    
    const newCardId = uuidv4();
    
    const newCard: CardType = {
      id: newCardId,
      content: '',
      position: {
        x: Math.random() * (windowSize.width - DEFAULT_CARD_WIDTH - 50) + 25,
        y: Math.random() * (windowSize.height - DEFAULT_CARD_HEIGHT - 50) + 25,
      },
      size: {
        width: DEFAULT_CARD_WIDTH,
        height: DEFAULT_CARD_HEIGHT
      },
      color: '#ffffff',
      tags: [],
      zIndex: newZIndex,
    };
    
    // カードが一定数を超えたらZインデックスを再整理
    if (cards.length >= 50) {
      setCards(prevCards => {
        const updatedCards = [...prevCards, newCard];
        reorganizeZIndices();
        return updatedCards;
      });
    } else {
      setCards([...cards, newCard]);
    }
    
    setLastAddedCardId(newCardId);
    
    // 次のレンダリングサイクルでフォーカスが適用された後、IDをリセット
    setTimeout(() => {
      setLastAddedCardId(null);
    }, 100);
  };

  // カードの内容を変更
  const handleContentChange = (id: string, content: string) => {
    setCards(
      cards.map((card) => (card.id === id ? { ...card, content } : card))
    );
    bringToFront(id);
  };

  // カードの色を変更
  const handleColorChange = (id: string, color: CardColor) => {
    const colorMap: Record<CardColor, string> = {
      white: '#ffffff',
      blue: '#bbdefb',
      green: '#c8e6c9',
      yellow: '#fff9c4',
      red: '#ffcdd2',
      purple: '#e1bee7',
    };

    setCards(
      cards.map((card) => (card.id === id ? { ...card, color: colorMap[color] } : card))
    );
    bringToFront(id);
  };

  // カードのサイズを変更
  const handleResize = (id: string, width: number, height: number) => {
    // 前回と同じサイズなら更新しない
    const card = cards.find(c => c.id === id);
    if (card && card.size?.width === width && card.size?.height === height) {
      return;
    }
    
    // ウィンドウサイズを考慮して最大サイズを制限
    const maxWidth = Math.min(width, windowSize.width - 50);
    const maxHeight = Math.min(height, windowSize.height - 50);
    
    // 最小サイズも保証
    const finalWidth = Math.max(200, maxWidth);
    const finalHeight = Math.max(150, maxHeight);
    
    setCards(prevCards => {
      return prevCards.map(card => {
        if (card.id === id) {
          return {
            ...card,
            size: {
              width: finalWidth,
              height: finalHeight
            }
          };
        }
        return card;
      });
    });
    
    // リサイズしたカードを最前面に
    bringToFront(id);
  };

  // カードにタグを追加
  const handleTagAdd = (id: string, tag: string) => {
    setCards(
      cards.map((card) =>
        card.id === id && !card.tags.includes(tag)
          ? { ...card, tags: [...card.tags, tag] }
          : card
      )
    );
    bringToFront(id);
  };

  // カードからタグを削除
  const handleTagRemove = (id: string, tag: string) => {
    setCards(
      cards.map((card) =>
        card.id === id
          ? { ...card, tags: card.tags.filter((t) => t !== tag) }
          : card
      )
    );
    
    // 削除したタグが現在のフィルターの場合、フィルターを解除
    if (tag === activeTag) {
      const cardWithTag = cards.find(c => c.id !== id && c.tags.includes(tag));
      if (!cardWithTag) {
        setActiveTag(null);
      }
    }
    
    bringToFront(id);
  };

  // カードを削除
  const handleCardDelete = (id: string) => {
    setCards(prevCards => prevCards.filter(card => card.id !== id));
    
    // 削除したカードのタグが現在のフィルターの場合、フィルターを解除
    if (activeTag) {
      const deletedCard = cards.find(card => card.id === id);
      if (deletedCard && deletedCard.tags.includes(activeTag)) {
        const otherCardWithTag = cards.find(card => card.id !== id && card.tags.includes(activeTag));
        if (!otherCardWithTag) {
          setActiveTag(null);
        }
      }
    }
  };

  // フィルタリングされたカード
  const filteredCards = getFilteredCards();
  // すべてのタグ
  const allTags = getAllTags();

  return (
    <BoardContainer>
      <DndContext sensors={sensors} onDragEnd={handleDragEnd} modifiers={[restrictToWindowEdges]}>
        {filteredCards.map((card) => (
          <Card
            key={card.id}
            card={card}
            onContentChange={handleContentChange}
            onColorChange={handleColorChange}
            onTagAdd={handleTagAdd}
            onTagRemove={handleTagRemove}
            onDelete={handleCardDelete}
            onResize={handleResize}
            onBringToFront={bringToFront}
            isNewlyAdded={card.id === lastAddedCardId}
          />
        ))}
      </DndContext>
      
      {allTags.length > 0 && (
        <TagFilter
          tags={allTags}
          activeTag={activeTag}
          onTagClick={filterByTag}
          onShowAllClick={() => setActiveTag(null)}
        />
      )}
      
      <AddCardButton onClick={addNewCard} />
      <ResetButton onClick={resetBoard} />
    </BoardContainer>
  );
};

export default Board; 